import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  ArrowLeftIcon,
  UserIcon,
  CalendarIcon,
  FlagIcon,
  MapPinIcon
} from '@heroicons/react/24/outline';
import apiService from '../services/apiService';
import ImageUpload from '../components/ImageUpload';

const getApiUrl = (endpoint) => {
  const baseUrl = process.env.NODE_ENV === 'production' ? 'https://fciveria.ge' : 'http://localhost:5000';
  return `${baseUrl}${endpoint}`;
};

const PlayerForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);
  
  const [formData, setFormData] = useState({
    name: '',
    position: '',
    number: '',
    age: '',
    nationality: 'საქართველო',
    team_type: 'first_team',
    photo_url: '',
    bio: '',
    height: '',
    weight: '',
    preferred_foot: '',
    contract_start: '',
    contract_end: ''
  });
  
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const teamTypes = [
    { value: 'first_team', label: 'პირველი გუნდი' },
    { value: 'second_team', label: 'მეორე გუნდი' },
    { value: 'womans_team', label: 'ქალთა გუნდი' }
  ];

  const positions = [
    { value: 'მეკარე', label: 'მეკარე' },
    { value: 'მცველი', label: 'მცველი' },
    { value: 'ნახევარმცველი', label: 'ნახევარმცველი' },
    { value: 'თავდამსხმელი', label: 'თავდამსხმელი' }
  ];

  const preferredFeet = [
    { value: 'left', label: 'მარცხენა' },
    { value: 'right', label: 'მარჯვენა' },
    { value: 'both', label: 'ორივე' }
  ];

  useEffect(() => {
    if (isEdit) {
      loadPlayer();
    }
  }, [id, isEdit]);

  const loadPlayer = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('fciveria_admin_user') 
        ? JSON.parse(localStorage.getItem('fciveria_admin_user')).token 
        : null;

      const response = await fetch(getApiUrl(`/api/players/${id}`), {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const player = await response.json();
        setFormData({
          name: player.name || '',
          position: player.position || '',
          number: player.number || '',
          age: player.age || '',
          nationality: player.nationality || 'საქართველო',
          team_type: player.team_type || 'first_team',
          photo_url: player.photo_url || '',
          bio: player.bio || '',
          height: player.height || '',
          weight: player.weight || '',
          preferred_foot: player.preferred_foot || '',
          contract_start: player.contract_start || '',
          contract_end: player.contract_end || ''
        });
      } else {
        console.error('Failed to load player');
        navigate('/_panel-7c39f8/players');
      }
    } catch (error) {
      console.error('Error loading player:', error);
      navigate('/_panel-7c39f8/players');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.position || !formData.team_type) {
      alert('გთხოვთ შეავსოთ ყველა სავალდებულო ველი');
      return;
    }

    try {
      setSaving(true);
      const token = localStorage.getItem('fciveria_admin_user') 
        ? JSON.parse(localStorage.getItem('fciveria_admin_user')).token 
        : null;

      const url = isEdit ? `/api/players/${id}` : '/api/players/';
      const method = isEdit ? 'PUT' : 'POST';

      // Prepare data for submission
      const submitData = {
        ...formData,
        number: formData.number ? parseInt(formData.number) : null,
        age: formData.age ? parseInt(formData.age) : null,
        height: formData.height ? parseInt(formData.height) : null,
        weight: formData.weight ? parseInt(formData.weight) : null,
        contract_start: formData.contract_start || null,
        contract_end: formData.contract_end || null
      };

      const response = await fetch(getApiUrl(url), {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(submitData)
      });

      if (response.ok) {
        navigate('/_panel-7c39f8/players');
      } else {
        const error = await response.json();
        alert(error.error || 'შეცდომა ფეხბურთელის შენახვისას');
      }
    } catch (error) {
      console.error('Error saving player:', error);
      alert('შეცდომა ფეხბურთელის შენახვისას');
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600 font-georgian">იტვირთება...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div className="flex items-center">
              <button
                onClick={() => navigate('/_panel-7c39f8/players')}
                className="mr-4 text-gray-400 hover:text-gray-600"
              >
                <ArrowLeftIcon className="h-6 w-6" />
              </button>
              <UserIcon className="h-8 w-8 text-primary-600 mr-3" />
              <div>
                <h1 className="text-2xl font-bold text-gray-900 font-georgian">
                  {isEdit ? 'ფეხბურთელის რედაქტირება' : 'ახალი ფეხბურთელი'}
                </h1>
                <p className="text-sm text-gray-600 font-georgian">
                  {isEdit ? 'ფეხბურთელის ინფორმაციის განახლება' : 'ახალი ფეხბურთელის დამატება'}
                </p>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-3xl mx-auto py-6 sm:px-6 lg:px-8">
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="bg-white shadow rounded-lg">
            <div className="px-4 py-5 sm:p-6">
              <h3 className="text-lg leading-6 font-medium text-gray-900 font-georgian mb-6">
                ძირითადი ინფორმაცია
              </h3>
              
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                <div>
                  <label className="block text-sm font-medium text-gray-700 font-georgian mb-2">
                    სახელი და გვარი *
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                    placeholder="ფეხბურთელის სახელი"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 font-georgian mb-2">
                    პოზიცია *
                  </label>
                  <select
                    name="position"
                    value={formData.position}
                    onChange={handleChange}
                    required
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                  >
                    <option value="">აირჩიეთ პოზიცია</option>
                    {positions.map(pos => (
                      <option key={pos.value} value={pos.value}>
                        {pos.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 font-georgian mb-2">
                    ნომერი
                  </label>
                  <input
                    type="number"
                    name="number"
                    value={formData.number}
                    onChange={handleChange}
                    min="1"
                    max="99"
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                    placeholder="მაისურის ნომერი"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 font-georgian mb-2">
                    ასაკი
                  </label>
                  <input
                    type="number"
                    name="age"
                    value={formData.age}
                    onChange={handleChange}
                    min="1"
                    max="50"
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                    placeholder="ასაკი წლებში"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 font-georgian mb-2">
                    ეროვნება
                  </label>
                  <input
                    type="text"
                    name="nationality"
                    value={formData.nationality}
                    onChange={handleChange}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                    placeholder="ეროვნება"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 font-georgian mb-2">
                    გუნდი *
                  </label>
                  <select
                    name="team_type"
                    value={formData.team_type}
                    onChange={handleChange}
                    required
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                  >
                    {teamTypes.map(team => (
                      <option key={team.value} value={team.value}>
                        {team.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white shadow rounded-lg">
            <div className="px-4 py-5 sm:p-6">
              <h3 className="text-lg leading-6 font-medium text-gray-900 font-georgian mb-6">
                დამატებითი ინფორმაცია
              </h3>
              
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                <div>
                  <label className="block text-sm font-medium text-gray-700 font-georgian mb-2">
                    სიმაღლე (სმ)
                  </label>
                  <input
                    type="number"
                    name="height"
                    value={formData.height}
                    onChange={handleChange}
                    min="150"
                    max="220"
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                    placeholder="სიმაღლე სანტიმეტრებში"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 font-georgian mb-2">
                    წონა (კგ)
                  </label>
                  <input
                    type="number"
                    name="weight"
                    value={formData.weight}
                    onChange={handleChange}
                    min="50"
                    max="120"
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                    placeholder="წონა კილოგრამებში"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 font-georgian mb-2">
                    ფავორიტი ფეხი
                  </label>
                  <select
                    name="preferred_foot"
                    value={formData.preferred_foot}
                    onChange={handleChange}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                  >
                    <option value="">აირჩიეთ ფეხი</option>
                    {preferredFeet.map(foot => (
                      <option key={foot.value} value={foot.value}>
                        {foot.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="sm:col-span-2">
                  <ImageUpload
                    value={formData.photo_url}
                    onChange={(url) => setFormData(prev => ({ ...prev, photo_url: url }))}
                    category="photos"
                    label="ფეხბურთელის ფოტო"
                    required={false}
                    maxSize={50}
                  />
                </div>
              </div>

              <div className="mt-6">
                <label className="block text-sm font-medium text-gray-700 font-georgian mb-2">
                  ბიოგრაფია
                </label>
                <textarea
                  name="bio"
                  value={formData.bio}
                  onChange={handleChange}
                  rows={4}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                  placeholder="ფეხბურთელის ბიოგრაფია..."
                />
              </div>
            </div>
          </div>

          <div className="bg-white shadow rounded-lg">
            <div className="px-4 py-5 sm:p-6">
              <h3 className="text-lg leading-6 font-medium text-gray-900 font-georgian mb-6">
                კონტრაქტის ინფორმაცია
              </h3>
              
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                <div>
                  <label className="block text-sm font-medium text-gray-700 font-georgian mb-2">
                    კონტრაქტის დაწყება
                  </label>
                  <input
                    type="date"
                    name="contract_start"
                    value={formData.contract_start}
                    onChange={handleChange}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 font-georgian mb-2">
                    კონტრაქტის დასრულება
                  </label>
                  <input
                    type="date"
                    name="contract_end"
                    value={formData.contract_end}
                    onChange={handleChange}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Submit Button */}
          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={() => navigate('/_panel-7c39f8/players')}
              className="bg-gray-300 hover:bg-gray-400 text-gray-800 px-4 py-2 rounded-md text-sm font-medium font-georgian"
            >
              გაუქმება
            </button>
            <button
              type="submit"
              disabled={saving}
              className="bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-md text-sm font-medium font-georgian disabled:opacity-50"
            >
              {saving ? 'შენახვა...' : (isEdit ? 'განახლება' : 'დამატება')}
            </button>
          </div>
        </form>
      </main>
    </div>
  );
};

export default PlayerForm;
